import { Dispatch, MutableRefObject, SetStateAction } from "react";
import { ButtonTypes, SelectFieldOption_Values } from "../../../Definitions";
import { FeedComponentInfo } from "../../../Services/Agrisys_GqlDataCaller/FeedDataCalls/GQLCalls/getFeedComponentsCall";
import { FeedCurve, FeedCurveComponent } from "../../../Services/Agrisys_GqlDataCaller/FeedDataCalls/GQLCalls/getFeedCurvesCall";
import { MaintainFeedCurve } from "../../../Services/Agrisys_GqlDataCaller/FeedDataCalls/GQLCalls/maintainFeedCurveCall";
import { colors, glyphs } from "../../../Styles";
import { Button } from "../../BaseComponents";
import { GenericRow } from "../../Tables/_Tables/useColumnConstructor";
import { cloneDeep, partition } from "lodash";

export interface FeedCurveTableData extends GenericRow {
  milestone?: string,
  milestoneId: number,
  id: number,
  dsWeight: number,
  totalAmount: number,
  nonNormalizedTotalAmount: number,
  day: number,
  addDay: JSX.Element,
  dayIndex: number,
  milestoneFirstDay: number;
  milestoneIndex: number;
  milestoneArrayIndex: number;
}

export const convertFeedCurveToTableData = (feedCurve: FeedCurve, setFeedCurve: Dispatch<SetStateAction<FeedCurve>>, createDayIdRef: MutableRefObject<number>, createMilestoneIdRef: MutableRefObject<number>) => {
  let firstDay = 0;
  let lastDayTracker = 0;
  let feedDayTracker = 0;
  const data = new Map<number, FeedCurveTableData>();
  const milestones = feedCurve.milestones;
  const components = feedCurve.components;
  if (milestones && milestones.length && components) {
    milestones.sort((milestoneA, milestoneB) => (milestoneA.milestoneIndex - milestoneB.milestoneIndex)).forEach((milestone, milestoneArrayIndex) => {
      lastDayTracker = milestoneArrayIndex === 0 ? feedDayTracker : feedDayTracker+1;
      if (milestoneArrayIndex !== 0) {
        firstDay = (lastDayTracker);
      }
      milestone.feedDays.sort((dayA, dayB) => (dayA.day-dayB.day)).forEach((feedDay, dayIndex, array) => {
        if (milestoneArrayIndex === 0 && dayIndex === 0) {
          firstDay = feedDay.day;
        }
        feedDayTracker = (feedDay.day + lastDayTracker);
        const dayTotal = feedDay.dayComponents.reduce((accumulator, component) => {if (feedDay.day === 0){} return accumulator += component.amount},0)
        let dsWeight = 0;
        const dayComponents = components.map((component) => {
          const dayComponent = feedDay.dayComponents.find((dayComponent) => dayComponent.componentId === component.componentId);
          if (dayComponent) {
            dsWeight += dayComponent.amount*component.ds;
            return {[String(component.componentId)]:  dayComponent.amount}
          }
          return {[String(component.componentId)]: undefined}
        })
        data.set( feedDay.dayId, {
          addDay: 
            <Button
              onClick={() => {
                console.log(-createDayIdRef.current);
                array.push({day: (milestone.feedDays.at(-1)?.day??0)+1, dayId: -createDayIdRef.current, dayComponents: []})
                createDayIdRef.current += 1;
                setFeedCurve(cloneDeep(feedCurve));
              }}
              startEnhancer={glyphs.Add({fillColor: colors.greyDarkSecondary, size: "10px"})}
              content=""
              buttonType={ButtonTypes.textButton}
              overrides={{startEnhancerStyles: {margin: "0px"}, contentStyles: {color: colors.greyDarkPrimary}, baseButton: {visibility: dayIndex !== (array.length - 1)  ? "hidden" : "visible"}}}
              />,
          milestoneIndex: milestone.milestoneIndex,
          milestoneArrayIndex,
          milestoneFirstDay: firstDay,
          milestone: dayIndex === 0 ? {label: milestone.name, id: milestone.milestoneId} : undefined,
          milestoneId: milestone.feedCurveMilestoneId,
          id: feedDay.dayId,
          day: feedDayTracker,
          dayIndex,
          dsWeight,
          ds: dayTotal === 0 ? 0 : Math.round((dsWeight/dayTotal)*100)/100,
          totalAmount: Math.round((dayTotal/1000)*100)/100,
          nonNormalizedTotalAmount: dayTotal,
          ...Object.assign({}, ...dayComponents)
        })
      } )
    });
  }
  if (milestones?.length === 0) {
    feedCurve.milestones = [{milestoneId: -createMilestoneIdRef.current, feedCurveMilestoneId: -createMilestoneIdRef.current, name: "", milestoneIndex: 1, feedDays: [{day: 0, dayId: -createDayIdRef.current, dayComponents: []}]}]
    createDayIdRef.current += 1;
    createMilestoneIdRef.current += 1;
    setFeedCurve(cloneDeep(feedCurve))
  }
  return data;
}

export const handlePercentileEdit = (data: Map<number, FeedCurveTableData>, feedCurveComponents: FeedCurveComponent[] | undefined, isPercentileEdit: Boolean) => {
  const dataCopy = new Map(data);
  if (!feedCurveComponents) {
    return dataCopy
  }
  dataCopy.forEach((row, key) => {
    const dayComponents = feedCurveComponents.map((component) => {
      const rowValue = row[component.componentId]
      if (rowValue !== undefined) {
        return {
          [String(component.componentId)]: isPercentileEdit ? Math.round((+rowValue/row.nonNormalizedTotalAmount)*100)/100: Math.round((+rowValue/1000)*100)/100 
        }
      }
      return {[String(component.componentId)]: undefined}
    })
    dataCopy.set(key, {...row, ...Object.assign({}, ...dayComponents)})
  })
  return dataCopy;
}

export function valueIsSelectFieldOptionsValue(value: string | number | boolean | SelectFieldOption_Values | undefined, accessor: string): value is SelectFieldOption_Values {
  return accessor === "milestone" && value !== undefined;
}

export function valueIsNumberValue(value: string | number | boolean | SelectFieldOption_Values | undefined, accessor: string): value is number {
  return accessor !== "milestone" && value !== undefined && value !== "";
}


export const handleNewFeedComponent = ({ feedComponentId, feedCurve, feedComponents, dirtRef }: { feedComponentId?: number; feedCurve: FeedCurve; feedComponents?: FeedComponentInfo[]; dirtRef: React.MutableRefObject<MaintainFeedCurve>; }) => {
  if (feedComponentId === undefined || feedComponents === undefined) {
    return feedCurve;
  }
  const feedComponent = feedComponents.find((feedComponent) => feedComponent.id === feedComponentId);
  if (feedComponent) {
    if (feedCurve.components === undefined) {
      feedCurve.components = []
    }
    feedCurve.components.push({limit: 0, gramPerMinute: 0, componentId: feedComponent.id, name: feedComponent.name, ds: feedComponent.ds, feedCurveComponentId: -feedComponent.id })
  
  if (dirtRef.current.creates === undefined) {
    dirtRef.current.creates = {};
  }
  if (dirtRef.current.creates.components === undefined) {
    dirtRef.current.creates.components = [];
  }
  dirtRef.current.creates.components.push({component_id: feedComponent?.id, temporaryId: feedComponent?.id, gram_per_minute: 0})
  }
  return feedCurve;
}

export const handleRemoveComponent = ({ componentId, feedCurveComponentId, componentIndex, feedCurve, dirtRef }: { componentId: number; feedCurveComponentId: number; componentIndex: number; feedCurve: FeedCurve; dirtRef: MutableRefObject<MaintainFeedCurve>; }): FeedCurve=> {
  feedCurve.components?.splice(componentIndex, 1);
  if (feedCurveComponentId < 0) {
    if (dirtRef.current.creates?.components) {
      const feedCurveComponentIndex = dirtRef.current.creates.components.findIndex((component) => component.temporaryId === feedCurveComponentId);
      dirtRef.current.creates.components.splice(feedCurveComponentIndex, 1);
    }
  } else {
    if (dirtRef.current.deletes === undefined) {
      dirtRef.current.deletes = {};
    }
    if (dirtRef.current.deletes.components === undefined) {
      dirtRef.current.deletes.components = [];
    }
    dirtRef.current.deletes.components.push(feedCurveComponentId);
  }
  feedCurve.milestones?.forEach((milestone) => {
    milestone.feedDays.forEach((feedDay) => {
    const [daysToKeep, daysToRemove]  = partition(feedDay.dayComponents, (dayComponent) => !(dayComponent.componentId===componentId))
    feedDay.dayComponents = daysToKeep;
    daysToRemove.forEach((dayComponent) => {
      if (dayComponent.dayComponentId < 0) {
        //remove from creates
        dirtRef.current.creates?.milestones?.forEach((dirtyMilestone) => {
          dirtyMilestone.feedDays?.forEach((dirtyFeedDay) => {
            dirtyFeedDay.dayComponents = dirtyFeedDay.dayComponents?.filter((dayComponent) => (!(dayComponent.component_id === componentId)));
          })
        })
      } else {
        if (dirtRef.current.deletes === undefined) {
          dirtRef.current.deletes = {}
        }
        if (dirtRef.current.deletes.dayComponents === undefined) {
          dirtRef.current.deletes.dayComponents = []
        }

        dirtRef.current.deletes.dayComponents.push(dayComponent.dayComponentId);
      } 

    })
  })
  })
  return cloneDeep(feedCurve);
};